import PropTypes from "prop-types"
import cx from "classnames"
import { RawLink } from "components/Link"
import { Text } from "components/Text"
import { useTranslator } from "components/Translator"
import { FileWordRegular } from "components/Icon/FileWordRegular"
import { FilePdfRegular } from "components/Icon/FilePdfRegular"
import { FileImageRegular } from "components/Icon/FileImageRegular"
import { FileAltRegular } from "components/Icon/FileAltRegular"
import { Pen } from "components/Icon/Pen"
import { TrashAlt } from "components/Icon/TrashAlt"
import { Card, CardContent } from "components/Card"
import { Button } from "components/Button"
import { ConfirmButton } from "components/ConfirmButton"
import { useRouter } from "components/Router"
import { useAsync } from "@react-hook/async"
import { Alerter } from "components/Alert"
import last from "lodash/last"
import { useConnectedUser } from "../ConnectedUserProvider"

function getFileUrlExtension(fileUrl) {
  const extensionArray = fileUrl.split(".")
  const extension = last(extensionArray)
  return extension
}

const getIconComponentForExtension = (fileExtension) => {
  if (!fileExtension) {
    return null
  }

  switch (fileExtension) {
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "bmp":
    case "webp":
    case "tiff":
      return FileImageRegular

    case "pdf":
      return FilePdfRegular

    case "doc":
    case "docx":
      return FileWordRegular

    default:
      return FileAltRegular
  }
}

function FiletypeIcon(props) {
  const { fileExtension, className } = props
  const Component = getIconComponentForExtension(fileExtension)

  return <Component className={className} />
}

function DeleteDocumentButton({ document, onDocumentDelete }) {
  const router = useRouter()
  const translator = useTranslator()

  const [{ status }, deleteDocument] = useAsync(async () => {
    const url = router.generate("app_user_document_delete", {
      documentId: document.id,
    })

    const response = await fetch(url, {
      method: "delete",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
    const data = await response.json()

    if (response.ok) {
      if (data.message) {
        Alerter.success(data.message)
      }

      onDocumentDelete(document)
    } else {
      if (data.message) {
        Alerter.error(data.message)
      }
    }
  })

  const isLoading = status === "loading"

  return (
    <ConfirmButton
      iconLeft={<TrashAlt className="w-4" />}
      variant="outline"
      color="danger"
      busy={isLoading}
      disabled={isLoading}
      onConfirm={deleteDocument}
      confirmTitle={translator.trans(
        "DocumentCard.delete.confirmTitle",
        null,
        "components",
      )}
      confirmContent={
        <Text tag="p">
          {translator.trans(
            "DocumentCard.delete.confirmContent",
            null,
            "components",
          )}
        </Text>
      }
      confirmLabel={translator.trans(
        "DocumentCard.delete.confirmLabel",
        null,
        "components",
      )}
    >
      {translator.trans("DocumentCard.delete.cta", null, "components")}
    </ConfirmButton>
  )
}

export function DocumentCard(props) {
  const { document, handleDeleteDocument, ownerId } = props
  const { docFile, label, updatedAt } = document
  const connectedUser = useConnectedUser()
  const translator = useTranslator()
  const docFileUrlExtension = getFileUrlExtension(docFile.url)

  return (
    <Card>
      <RawLink href={docFile.url} external>
        <CardContent className="flex flex-col space-y-4 justify-center">
          <div className="flex flex-row space-x-4">
            <div>
              <FiletypeIcon
                className="w-12 text-primary-default"
                fileExtension={docFileUrlExtension}
              />
            </div>
            <div>
              <div
                className={cx({
                  "flex flex-col space-y-2 justify-center": label,
                })}
              >
                {label ? (
                  <Text variant="headline4" className="truncate">
                    {label}
                  </Text>
                ) : null}
                <Text variant="caption" className="text-light">
                  {new Date(updatedAt).toLocaleString(translator.locale)}
                </Text>
              </div>
            </div>
          </div>
          <div className="space-y-2 lg:space-y-0 lg:space-x-2 w-full flex lg:flex-row lg:items-center lg:justify-center flex-col">
            {connectedUser?.id === ownerId ? (
              <>
                <Button
                  href={{
                    route: "app_user_document_edit",
                    params: { documentId: document.id },
                  }}
                  iconLeft={<Pen className="w-4" />}
                  variant="outline"
                  color="secondary"
                >
                  {translator.trans("DocumentCard.edit", null, "components")}
                </Button>
                <DeleteDocumentButton
                  document={document}
                  onDocumentDelete={handleDeleteDocument}
                />
              </>
            ) : null}
          </div>
        </CardContent>
      </RawLink>
    </Card>
  )
}

DocumentCard.propTypes = {
  document: PropTypes.shape({
    docFile: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }).isRequired,
    label: PropTypes.string,
    updatedAt: PropTypes.string.isRequired,
  }).isRequired,
}
